import { Injectable } from '@angular/core';
import { getStoredProjectLogo } from '@app/utils/project-logo.util';
import { HttpParams } from '@angular/common/http';
import { Project } from '@app/models/firebase/project';
import { IDomainConnectDetect, IDomainConnectLinkInfo, IDomainConnectSetupLink, IDomainConnectStart, IDomainConnectStatus } from '@app/models/firebase/domain-connect';
import { DbService } from '../db/db.service';
import { getFileNameExtension, isNullOrUndefined } from '@pineapplelab/util';
import { StorageService } from '../storage/storage.service';
import { ApiService } from '../api/api.service';
import { API_URLS } from '@app/utils/urls';
import { AuthService } from '../auth/auth.service';
import { UserRole } from '@app/models/firebase/userRole';
import { User } from '@app/models/firebase/user';
import { ProjectFirestoreService } from '../base/project-firestore.service';
import { IBillSettings } from '@app/interfaces/bill-settings.interface';
import { stripUndefined } from '@app/utils/strip-undefined.util';

@Injectable({
  providedIn: 'root'
})
export class ProjectService extends ProjectFirestoreService {

  currentData: { project: Project } = {
    project: null
  };

  projectLogo = getStoredProjectLogo();

  constructor(
    db: DbService,
    private storageService: StorageService,
    private api: ApiService,
    private authService: AuthService
  ) {
    super(db);
  }

  async getCurrentProject(projectId: string) {
    const projectDoc = await this.projectDoc(projectId, Project.PATH).get();
    if (projectDoc.exists) {
      this.currentData.project = new Project( projectDoc.data() );
    }
    return projectDoc.exists
  }

  async getAllProjects() {
    const projects = await this.db.firestore.collection(Project.PATH).get({ source: 'server' });
    return projects.docs.map( doc => new Project( doc.data() ) );
  }


  async getProjectByURL(url: string) {
    const project = await this.db.firestore.collection(Project.PATH).where('url', '==', url).get();
    if (project.empty){
      return null;
    }
    return new Project( project.docs[0].data() );
  }

  async save(model: Project) {
    if (isNullOrUndefined(model.id)) {
      model.id = this.authService.getProjectIdFromHostname(model.url);
    }
    if (!model.inboundEmailAddress) {
      model.inboundEmailAddress = `bills+${model.id}@inbound.pineapplelab.com`;
    }
    if (!isNullOrUndefined( model?.__fileLogoImage )){ await this.saveOrRemoveImagesIfNecessary(model) }
    if (!isNullOrUndefined( model?.favicon?.__file )){ await this.saveOrRemoveFaviconIfNecessary(model, this.authService.projectId) }
    const json = model.toJson();
    await this.projectDoc(model.id, Project.PATH).set(json, { merge: true });
    return json;
  }

  async saveOrRemoveImagesIfNecessary(model: Project) {
    const commonPath = `logos/${model.id}`;

    if (!isNullOrUndefined( model.__fileLogoImage )) {
      const path = `${commonPath}/image.${getFileNameExtension( model.__fileLogoImage.name )}`;
      model.logo = await this.storageService.uploadFile( path, model.__fileLogoImage, {contentType: 'image/jpeg'}  );
    } else if (model.__fileLogoImage === null) {
      model.logo = null;
    }
  }
  async saveOrRemoveFaviconIfNecessary(model: Project, projectId: string) {
    const commonPath = `${Project.PATH}/${projectId}/imageBanner`;

    if (!isNullOrUndefined( model.favicon.__file )) {
      const path = `${commonPath}/favicon.png&alt=media`;
      model.favicon.img = await this.storageService.uploadFile( path, model.favicon.__file, {contentType: 'image/png'}  );
    } else if (model.favicon.__file === null) {
      model.favicon.img = null;
    }
  }

  remove(modelId: string) {
    return this.projectDoc(modelId, Project.PATH).delete();
  }

  async getProjectByUrl(url: string) {
    const project = await this.db.firestore.collection(Project.PATH).where('url', '==', url).get();
    if (project.docs.length === 0) {
      return null;
    }
    return new Project( project.docs[0].data() );
  }

  async getProjectById(id: string) {
    const project = await this.db.firestore.collection(Project.PATH).where('id', '==', id).get();
    if (project.docs.length === 0) {
      return null;
    }
    return new Project( project.docs[0].data() );
  }

  async generateThemeCSS(projectId: string): Promise<void> {
    try {
      await this.api.post(API_URLS.PROJECT.GENERATE_THEME(projectId), {});
    } catch (error) {
      console.log(error);
    }
  }

  async setupDomains(projectId: string): Promise<any> {
    return this.api.post(API_URLS.PROJECT.SETUP_DOMAINS(projectId), {});
  }

  async setupRecaptcha(projectId: string): Promise<any> {
    return this.api.post(API_URLS.PROJECT.SETUP_RECAPTCHA(projectId), {});
  }

  private unwrap<T>(res: any): T {
    return (res?.data ?? res) as T;
  }

  async domainConnectDetect(projectId: string): Promise<IDomainConnectDetect> {
    return this.unwrap(await this.api.get(API_URLS.DOMAIN_CONNECT.DETECT(projectId), new HttpParams()));
  }

  async domainConnectStart(projectId: string, acknowledgeReplacements = false): Promise<IDomainConnectStart> {
    // returnOrigin only tells the backend where the user is; the backend accepts the project's own app origin or the
    // canonical admin origin and ignores anything else.
    return this.unwrap(await this.api.post(API_URLS.DOMAIN_CONNECT.START(projectId), { acknowledgeReplacements, returnOrigin: window.location.origin }));
  }

  /** Unauthenticated: used by the canonical callback page, which has no session on its origin. */
  async domainConnectReturnTarget(state: string): Promise<{ origin: string; path: string }> {
    return this.unwrap(await this.api.get(API_URLS.DOMAIN_CONNECT.RETURN, new HttpParams().set('state', state)));
  }

  async domainConnectCreateLink(projectId: string): Promise<IDomainConnectSetupLink> {
    return this.unwrap(await this.api.post(API_URLS.DOMAIN_CONNECT.LINK(projectId), {}));
  }

  async domainConnectRevokeLinks(projectId: string): Promise<void> {
    await this.api.post(API_URLS.DOMAIN_CONNECT.LINK_REVOKE(projectId), {});
  }

  // Public (no session): the token in the shareable setup link is the only credential.
  async domainConnectLinkInfo(token: string): Promise<IDomainConnectLinkInfo> {
    return this.unwrap(await this.api.get(API_URLS.DOMAIN_CONNECT.PUBLIC_INFO(token), new HttpParams()));
  }

  async domainConnectLinkStart(token: string, acknowledgeReplacements = false): Promise<IDomainConnectStart> {
    return this.unwrap(await this.api.post(API_URLS.DOMAIN_CONNECT.PUBLIC_START(token), { acknowledgeReplacements }));
  }

  async domainConnectLinkStatus(token: string): Promise<IDomainConnectStatus> {
    return this.unwrap(await this.api.get(API_URLS.DOMAIN_CONNECT.PUBLIC_STATUS(token), new HttpParams()));
  }

  async domainConnectLinkComplete(token: string, body: { state: string; error?: string; error_description?: string }): Promise<IDomainConnectStatus> {
    return this.unwrap(await this.api.post(API_URLS.DOMAIN_CONNECT.PUBLIC_COMPLETE(token), body));
  }

  async domainConnectStatus(projectId: string): Promise<IDomainConnectStatus> {
    return this.unwrap(await this.api.get(API_URLS.DOMAIN_CONNECT.STATUS(projectId), new HttpParams()));
  }

  async domainConnectComplete(body: { projectId: string; state: string; error?: string; error_description?: string }): Promise<IDomainConnectStatus> {
    return this.unwrap(await this.api.post(API_URLS.DOMAIN_CONNECT.CALLBACK, body));
  }

  async domainConnectDisconnect(projectId: string): Promise<void> {
    await this.api.post(API_URLS.DOMAIN_CONNECT.DISCONNECT(projectId), {});
  }

  async saveAdminUser(projectId: string, user: User, userRole: UserRole): Promise<void> {
    let userResponse = await this.projectCollection(projectId, Project.PATH, User.PATH).doc(user.id).set(user.toJson(), { merge: true });
    let userRoleResponse = await this.projectCollection(projectId, Project.PATH, UserRole.PATH).doc(user.id).set(userRole.toJson(), { merge: true });
  }

  async updateTestingPhone(projectId: string, testingPhone: string | null): Promise<void> {
    await this.projectDoc(projectId, Project.PATH).set(
      { testingPhone: testingPhone ?? null },
      { merge: true },
    );
  }

  async getTestingPhone(projectId: string): Promise<string | null> {
    const doc = await this.projectDoc(projectId, Project.PATH).get();
    if (!doc.exists) return null;
    return doc.data()?.testingPhone ?? null;
  }

  async updateTestingEmail(projectId: string, testingEmail: string | null): Promise<void> {
    await this.projectDoc(projectId, Project.PATH).update({
      testingEmail: testingEmail || null,
    });
  }

  async getTestingEmail(projectId: string): Promise<string | null> {
    const doc = await this.projectDoc(projectId, Project.PATH).get({ source: 'server' });
    if (!doc.exists) return null;
    const data = doc.data();
    // Prefer direct write (camelCase); fall back to snake_case from project form toJson()
    // Use explicit undefined check so that a saved null value is honoured
    const val = data?.testingEmail !== undefined ? data.testingEmail : (data?.testing_email ?? null);
    return val || null;
  }

  async updateEmailSettings(projectId: string, emailSettings: any): Promise<void> {
    // Use dot-notation so chrome fields stored in emailSettings.* are not wiped
    const updates: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(emailSettings)) {
      updates[`emailSettings.${key}`] = value ?? null;
    }
    await this.projectDoc(projectId, Project.PATH).update(updates);
  }

  async updateEmailChrome(projectId: string, emailChrome: any): Promise<void> {
    // Save chrome/design fields inside emailSettings using dot-notation
    // so everything email-related lives in one Firestore field
    const chromeFields = ['logo', 'companyName', 'phone', 'address', 'websiteUrl', 'showUnsubscribe'];
    const updates: { [key: string]: any } = {};
    for (const field of chromeFields) {
      if (emailChrome[field] !== undefined) {
        updates[`emailSettings.${field}`] = emailChrome[field] ?? null;
      }
    }
    if (Object.keys(updates).length > 0) {
      await this.projectDoc(projectId, Project.PATH).update(updates);
    }
  }

  async getEmailChrome(projectId: string): Promise<any> {
    const projectDoc = await this.projectDoc(projectId, Project.PATH).get({ source: 'server' });
    if (!projectDoc.exists) return null;
    const data = projectDoc.data();
    // Chrome fields are now saved inside emailSettings; fall back to old emailChrome for existing projects
    const s = data?.emailSettings || {};
    const c = data?.emailChrome || {};
    return {
      logo:            s.logo            !== undefined ? s.logo            : (c.logo            ?? null),
      companyName:     s.companyName     !== undefined ? s.companyName     : (c.companyName     ?? null),
      phone:           s.phone           !== undefined ? s.phone           : (c.phone           ?? null),
      address:         s.address         !== undefined ? s.address         : (c.address         ?? null),
      websiteUrl:      s.websiteUrl      !== undefined ? s.websiteUrl      : (c.websiteUrl      ?? null),
      showUnsubscribe: s.showUnsubscribe  !== undefined ? s.showUnsubscribe : (c.showUnsubscribe !== undefined ? c.showUnsubscribe : true),
    };
  }

  async updateBillSettings(projectId: string, billSettings: IBillSettings): Promise<void> {
    await this.projectDoc(projectId, Project.PATH).update({
      billSettings: stripUndefined(billSettings)
    });
  }

  async getBillSettings(projectId: string): Promise<IBillSettings | null> {
    const projectDoc = await this.projectDoc(projectId, Project.PATH).get();
    if (projectDoc.exists) {
      return projectDoc.data()?.billSettings || null;
    }
    return null;
  }

  async getEmailSettings(projectId: string): Promise<any> {
    const projectDoc = await this.projectDoc(projectId, Project.PATH).get({ source: 'server' });
    if (projectDoc.exists) {
      const data = projectDoc.data();
      return data?.emailSettings || null;
    }
    return null;
  }

}
